import { InputAdornment } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getTruckPriceTypeConfig,
    getTruckRenterPriceTypeLabel,
    systemConfigKeyEnum,
    truckPriceTypeEnum,
    truckRenterPriceTypeEnum,
} from '../../../../constants';
import { useCreateDailyTruckMutation, useUpdateDailyTruckMutation } from '../../../../hooks/mutations/useDailyTruckMutation';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useTrucksQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyTruck } from '../../../../services/apiSLTP/dailyReports/dailyTrucks';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import IconButton from '../../../ui/IconButton';
import QuantityToggleGroup from '../../../ui/QuantityToggleGroup';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyTruckModalProps extends ModalProps {
    refetch: () => void;
    dailyTruck: DailyTruck | null;
    activities: OptionType[];
    siteName?: string;
    isOwnCompany: boolean;
}

const ManageValidationDailyTruckModal: React.FC<ManageValidationDailyTruckModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTruck,
    activities,
    siteName,
    isOwnCompany,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyTruckId] = useState(dailyTruck?._id || '');
    const [selectedTruck, setSelectedTruck] = useState<OptionType>({
        label: dailyTruck?.truckId.designation
            ? dailyTruck.truckId.truckCompanyId.isOwnCompany
                ? dailyTruck.truckId.designation
                : `[${dailyTruck.truckId.plateNumber}] ${dailyTruck.truckId.truckCompanyId.designation} ${dailyTruck.truckId.truckTypeId.designation}`
            : '',
        value: dailyTruck?.truckId._id || '',
        group: dailyTruck?.truckId.truckTypeId.designation || '',
    });
    const [selectedTruckCompany, setSelectedTruckCompany] = useState({
        label: dailyTruck?.truckId.truckCompanyId.designation || '',
        value: dailyTruck?.truckId.truckCompanyId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyTruck?.activityId)?.label || '',
        value: dailyTruck?.activityId || '',
    });
    const [unitPrice, setUnitPrice] = useState<string>(dailyTruck?.unitPrice ? dailyTruck.unitPrice.toString() : '');
    const [quantity, setQuantity] = useState<string>(dailyTruck?.quantity ? dailyTruck.quantity.toString() : '0');
    const [priceType, setPriceType] = useState<OptionType | null>({
        label: typeof dailyTruck?.priceType === 'number' ? capitalizeFirstLetter(t(getTruckRenterPriceTypeLabel(dailyTruck.priceType))) : '',
        value: dailyTruck?.priceType?.toString() || '',
    });

    const { data: dataTrucks, isLoading: isTrucksLoading } = useTrucksQuery({
        queries: { active: true, truckCompanyId: selectedTruckCompany.value },
    });
    const { data: dataTruckCompanies, isLoading: isTrucksCompaniesLoading } = useTrucksCompaniesQuery({
        queries: { active: true },
    });

    const { mutate: createDailyTruck } = useCreateDailyTruckMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyTruck } = useUpdateDailyTruckMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const truckCompanies = useMemo(() => {
        return dataTruckCompanies?.filter((company) => !!company.isOwnCompany === isOwnCompany);
    }, [dataTruckCompanies, isOwnCompany]);

    const postDailyTruck = async () => {
        createDailyTruck({
            body: {
                activityId: selectedActivity.value,
                truckId: selectedTruck.value,
                unitPrice: unitPrice ? parseFloat(unitPrice) : undefined,
                priceType: priceType?.value ? parseInt(priceType.value) : undefined,
                quantity: parseFloat(quantity) ? parseFloat(quantity) : undefined,
            },
        });
    };

    const patchDailyTruck = async () => {
        if (currentDailyTruckId)
            updateDailyTruck({
                id: currentDailyTruckId,
                body: {
                    activityId: selectedActivity.value,
                    truckId: selectedTruck.value,
                    unitPrice: unitPrice ? parseFloat(unitPrice) : null,
                    priceType: priceType?.value ? parseInt(priceType.value) : null,
                    quantity: parseFloat(quantity) ? parseFloat(quantity) : null,
                },
            });
    };

    const numericPriceType = parseInt(priceType?.value ?? '');
    const { step, min, max, decimals } = getTruckPriceTypeConfig(numericPriceType);

    const updateQuantity = (delta: number) => {
        setQuantity((prev) => {
            const q = parseFloat(prev) || 0;
            const newValue = Math.min(max ?? 1000, Math.max(min, q + delta));
            return newValue.toFixed(decimals);
        });
    };

    useEffect(() => {
        if (!priceType?.value) return;

        setQuantity((prev) => {
            const q = parseFloat(prev) || 0;
            const rounded = Math.round(q / step) * step;
            const clamped = Math.min(max ?? 1000, Math.max(min, rounded));
            return clamped.toFixed(decimals);
        });
    }, [priceType]);

    return (
        <ModalBody
            title={isOwnCompany ? t('truck') : t('externalTruck')}
            subtitle={!!currentDailyTruckId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyTruckId.length ? postDailyTruck() : patchDailyTruck();
            }}
            disableSubmit={!selectedTruck.value.length || !selectedActivity.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedTruckCompany}
                items={
                    truckCompanies?.map((company) => ({
                        label: company.designation,
                        value: company._id,
                    })) || []
                }
                handleChange={(event, value) => {
                    if (value?.value !== selectedTruckCompany.value) {
                        setSelectedTruck({ value: '', label: '' });
                    }
                    setSelectedTruckCompany({ value: value?.value || '', label: value?.label || '' });
                }}
                inputLabel={t('truckCompany')}
                required
                loading={isTrucksCompaniesLoading}
            />
            <DropDown
                selectedItem={selectedTruck}
                items={
                    dataTrucks
                        ?.sort((a, b) => (a.truckTypeId.displayOrder ?? Infinity) - (b.truckTypeId.displayOrder ?? Infinity))
                        .map((truck) => ({
                            label: truck.truckCompanyId.isOwnCompany
                                ? truck.designation
                                : `[${truck.plateNumber}] ${truck.truckCompanyId.designation} ${truck.truckTypeId.designation}`,
                            value: truck._id,
                            group: truck.truckTypeId.designation,
                        })) || []
                }
                handleChange={(event, value) => setSelectedTruck({ value: value?.value || '', label: value?.label || '', group: value?.group || '' })}
                inputLabel={t('truck')}
                grouped
                required
                sortGroups={false}
                loading={isTrucksLoading}
            />
            <DropDown
                items={Object.values(!!isOwnCompany ? truckPriceTypeEnum : truckRenterPriceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getTruckRenterPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('unit')}
                disableClearable={false}
            />

            {numericPriceType === truckRenterPriceTypeEnum.DAY ? (
                <QuantityToggleGroup value={parseFloat(quantity)} onChange={(value) => setQuantity(value.toFixed(decimals))} />
            ) : (
                <CustomTextInput
                    label={capitalizeFirstLetter(t('quantity'))}
                    type="number"
                    value={quantity}
                    onChange={(text) => setQuantity(text)}
                    startAdornment={
                        <InputAdornment position="start">
                            <IconButton iconName="remove" disabled={parseFloat(quantity) <= min} onClick={() => updateQuantity(-step)} />
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton iconName="add" disabled={parseFloat(quantity) >= (max ?? 1000)} onClick={() => updateQuantity(step)} />
                        </InputAdornment>
                    }
                />
            )}

            {truckCompanies && !truckCompanies?.find((company) => company._id === selectedTruckCompany.value)?.isOwnCompany && (
                <CustomTextInput
                    value={unitPrice}
                    onChange={setUnitPrice}
                    label={t('unitPrice')}
                    startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                    type="number"
                />
            )}
        </ModalBody>
    );
};

export default ManageValidationDailyTruckModal;
